import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Star, Minus, Plus, Truck, ShieldCheck, Loader2 } from "lucide-react";
import { api, inr, sortedTiers, tierLabel, salePrice, tierDiscount } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { addToCart, inWishlist, toggleWishlist, user, setAuthOpen } = useStore();
  const [product, setProduct] = useState(null);
  const [pieces, setPieces] = useState("6");
  const [qty, setQty] = useState(1);
  const [activeImg, setActiveImg] = useState(0);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => api.get(`/products/${slug}`).then((r) => { setProduct(r.data); setActiveImg(0); setPieces(sortedTiers(r.data.prices)[0]); });
  useEffect(() => { load(); window.scrollTo(0, 0); }, [slug]);

  if (!product) return <div className="py-32 text-center text-[#8C7B6E]"><Loader2 className="animate-spin inline" /> Loading…</div>;

  const price = salePrice(product, pieces);
  const liked = inWishlist(product.product_id);

  const submitReview = async () => {
    if (!user) return setAuthOpen(true);
    setSubmitting(true);
    try {
      await api.post(`/products/${product.product_id}/reviews`, { rating, comment });
      toast.success("Thank you for your review!");
      setComment("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit review");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="product-detail" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10">
      <nav className="text-xs text-[#8C7B6E] mb-8"><Link to="/shop" className="hover:text-[#D4AF37]">Explore Collection</Link> / <span className="text-[#C8B8AB]">{product.name}</span></nav>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Gallery */}
        <div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="lux-card overflow-hidden aspect-[4/3] mb-4">
            <img src={product.images?.[activeImg]} alt={product.name} className="h-full w-full object-cover" data-testid="product-main-image" />
          </motion.div>
          <div className="grid grid-cols-4 gap-3">
            {product.images?.map((img, i) => (
              <button key={img} onClick={() => setActiveImg(i)} className={`aspect-square rounded-xl overflow-hidden border-2 transition ${activeImg === i ? "border-[#D4AF37]" : "border-transparent opacity-60 hover:opacity-100"}`}>
                <img src={img} alt="" className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div>
          <span className="eyebrow">{product.sku ? `${product.sku} · ` : ""}{product.collection || product.category}</span>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-tight">{product.name}</h1>
          <div className="flex items-center gap-3 mt-3">
            <div className="flex">{[1, 2, 3, 4, 5].map((s) => <Star key={s} size={16} fill={s <= Math.round(product.rating_avg) ? "#D4AF37" : "none"} stroke="#D4AF37" />)}</div>
            <span className="text-[#8C7B6E] text-sm">{product.rating_count} review{product.rating_count !== 1 ? "s" : ""}</span>
          </div>

          <p className="text-[#C8B8AB] mt-5 leading-relaxed">{product.long_description || product.description}</p>

          <div className="mt-8">
            <span className="text-xs text-[#8C7B6E] uppercase tracking-wider">Choose {product.pricing_type === "weight" ? "weight" : product.pricing_type === "single" ? "option" : "box size"}</span>
            <div className="flex flex-wrap gap-2 mt-3" data-testid="piece-selector">
              {sortedTiers(product.prices).map((p) => (
                <button key={p} data-testid={`piece-${p}`} onClick={() => setPieces(p)}
                  className={`px-4 py-3 rounded-xl text-center border transition ${pieces === p ? "bg-[#D4AF37] text-[#1A0F0B] border-[#D4AF37] font-bold" : "border-[#3B251D] text-[#C8B8AB] hover:border-[#D4AF37]"}`}>
                  <span className="block font-display text-base">{tierLabel(p)}</span>
                  {tierDiscount(product, p) > 0 && <span className="block text-[9px] font-bold text-[#D4AF37]" data-testid={`tier-discount-${p}`}>-{tierDiscount(product, p)}%</span>}
                  <span className="block text-[10px]">{inr(salePrice(product, p))}</span>
                </button>
              ))}
            </div>
            {product.flavours && <p className="text-[#8C7B6E] text-xs mt-3">{Array.isArray(product.flavours) ? `Includes: ${product.flavours.join(", ")}` : product.flavours}</p>}
          </div>

          <div className="flex items-end justify-between mt-8">
            <div>
              <span className="text-xs text-[#8C7B6E]">Price · {tierLabel(pieces)}</span>
              <div className="flex items-baseline gap-3">
                <span className="font-display text-4xl text-[#D4AF37]" data-testid="product-price">{inr(price)}</span>
                {tierDiscount(product, pieces) > 0 && <><span className="text-[#8C7B6E] line-through" data-testid="product-mrp">{inr(product.prices[pieces])}</span><span data-testid="product-discount-badge" className="text-xs font-bold bg-[#D4AF37] text-[#1A0F0B] px-2 py-0.5 rounded-full">{tierDiscount(product, pieces)}% OFF</span></>}
              </div>
            </div>
            <div className="flex items-center border border-[#3B251D] rounded-full">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="h-11 w-11 flex items-center justify-center text-[#D4AF37]" data-testid="qty-minus"><Minus size={16} /></button>
              <span className="w-10 text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="h-11 w-11 flex items-center justify-center text-[#D4AF37]" data-testid="qty-plus"><Plus size={16} /></button>
            </div>
          </div>

          <div className="flex gap-3 mt-6">
            <Button data-testid="add-to-cart-btn" disabled={product.stock <= 0} onClick={() => addToCart(product, pieces, qty)} className="btn-gold flex-1 h-14 rounded-full text-base">
              {product.stock <= 0 ? "Sold Out" : `Add to Cart · ${inr(price * qty)}`}
            </Button>
            <button data-testid="pdp-wishlist-btn" onClick={() => toggleWishlist(product.product_id)}
              className={`h-14 w-14 rounded-full border flex items-center justify-center transition ${liked ? "bg-[#D4AF37] text-[#1A0F0B] border-[#D4AF37]" : "border-[#3B251D] text-[#FDFBF7] hover:border-[#D4AF37]"}`}>
              <Heart size={20} fill={liked ? "#1A0F0B" : "none"} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-8 text-sm">
            <div className="flex items-center gap-2 text-[#C8B8AB]"><Truck size={18} className="text-[#D4AF37]" /> Free delivery over ₹3,500</div>
            <div className="flex items-center gap-2 text-[#C8B8AB]"><ShieldCheck size={18} className="text-[#D4AF37]" /> Secure Razorpay checkout</div>
          </div>

          <div className="lux-card p-5 mt-8">
            <h4 className="eyebrow mb-2">Composition</h4>
            <p className="text-[#C8B8AB] text-sm">{product.ingredients}</p>
            {product.additional_information && <p className="text-[#8C7B6E] text-xs mt-3 leading-relaxed" data-testid="product-storage-note">{product.additional_information}</p>}
          </div>
        </div>
      </div>

      {/* Reviews */}
      <section className="mt-20">
        <h2 className="font-display text-3xl mb-8">Reviews & Ratings</h2>
        <div className="grid lg:grid-cols-[1fr_380px] gap-10">
          <div className="space-y-4" data-testid="reviews-list">
            {product.reviews?.length === 0 && <p className="text-[#8C7B6E]">Be the first to review this creation.</p>}
            {product.reviews?.map((r) => (
              <div key={r.review_id} className="lux-card p-5">
                <div className="flex items-center justify-between">
                  <span className="font-display text-lg">{r.user_name}</span>
                  <div className="flex">{[1, 2, 3, 4, 5].map((s) => <Star key={s} size={13} fill={s <= r.rating ? "#D4AF37" : "none"} stroke="#D4AF37" />)}</div>
                </div>
                <p className="text-[#C8B8AB] text-sm mt-2">{r.comment}</p>
              </div>
            ))}
          </div>
          <div className="lux-card p-6 h-max">
            <h4 className="font-display text-xl mb-4">Write a review</h4>
            <div className="flex gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((s) => (
                <button key={s} onClick={() => setRating(s)} data-testid={`rate-${s}`}><Star size={26} fill={s <= rating ? "#D4AF37" : "none"} stroke="#D4AF37" /></button>
              ))}
            </div>
            <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience…" className="bg-[#1A0F0B] border-[#3B251D] text-[#FDFBF7] mb-4" />
            <Button data-testid="submit-review-btn" onClick={submitReview} disabled={submitting} className="btn-gold w-full rounded-full">
              {submitting ? <Loader2 className="animate-spin" size={16} /> : user ? "Submit Review" : "Sign in to Review"}
            </Button>
          </div>
        </div>
      </section>

      {/* Related */}
      {product.related?.length > 0 && (
        <section className="mt-20">
          <h2 className="font-display text-3xl mb-8">You May Also Love</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-7">
            {product.related.map((p, i) => <ProductCard key={p.product_id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
